import axios from 'axios';
import { useAuthStore } from '../store/auth';
import { showToast } from '../hooks/useToast';
import type { AdPlacement } from '../types';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

function getOrCreateVisitorId(): string | undefined {
  if (typeof window === 'undefined') return undefined;

  const storageKey = 'radar_jovem_visitor_id';
  const existing = window.localStorage.getItem(storageKey);
  if (existing) return existing;

  const generated = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `visitor-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

  window.localStorage.setItem(storageKey, generated);
  return generated;
}

// Add auth token to requests
apiClient.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  const requestUrl = String(config.url || '');
  const isLikeRequest = /\/articles\/[^/]+\/(liked|like|likes)(\b|\?|$)/.test(requestUrl);

  if (isLikeRequest) {
    const visitorId = getOrCreateVisitorId();
    if (visitorId) {
      config.headers['X-Visitor-Id'] = visitorId;
    }
  }

  return config;
});

// Handle token refresh on 401
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const requestUrl = String(originalRequest?.url || '');
    const isAuthRequest = /\/auth\/(login|refresh|logout)$/.test(requestUrl);
    const status = error.response?.status;
    const serverMessage = String(error.response?.data?.error || '').toLowerCase();
    const isTokenAuthFailure =
      status === 401 ||
      status === 403 ||
      serverMessage.includes('invalid token') ||
      serverMessage.includes('token expired') ||
      serverMessage.includes('jwt');

    // If the access token is expired/invalid, try to refresh it once.
    if (!isAuthRequest && isTokenAuthFailure && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const { refreshToken } = useAuthStore.getState();
        
        if (!refreshToken) {
          useAuthStore.getState().logout();
          return Promise.reject(error);
        }

        // Request new access token
        const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
          refreshToken,
        });

        const { accessToken, refreshToken: newRefreshToken } = response.data;

        // Update store with new tokens
        useAuthStore.getState().setToken(accessToken);
        useAuthStore.getState().setRefreshToken(newRefreshToken);

        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        useAuthStore.getState().logout();
        showToast('Sessão expirada. Por favor, faça login novamente.', 'error');
        return Promise.reject(refreshError);
      }
    }

    // Show server-provided error message when available.
    if (!isAuthRequest) {
      try {
        if (error.response?.data?.error) {
          showToast(error.response.data.error, 'error');
        }
      } catch (e) {
        // ignore
      }
    }

    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  login: (email: string, password: string) =>
    apiClient.post('/auth/login', { email, password }),
  register: (data: { email: string; password: string; name: string; role?: 'viewer' | 'writer' }) =>
    apiClient.post('/auth/register', data),
  getMe: () => apiClient.get('/auth/me'),
  refresh: (refreshToken: string) =>
    apiClient.post('/auth/refresh', { refreshToken }),
  logout: (refreshToken: string) =>
    apiClient.post('/auth/logout', { refreshToken }),
};

// Article APIs
export const articleAPI = {
  getArticles: (category?: string, limit = 20, offset = 0) =>
    apiClient.get('/articles', {
      params: {
        category: category && category !== 'Recentes' ? category : undefined,
        limit,
        offset,
      },
    }),
  getArticleBySlug: (slug: string) => apiClient.get(`/articles/${slug}`),
  getUserArticles: () => apiClient.get('/articles/me/all'),
  getArticleForEdit: (id: number) => apiClient.get(`/articles/edit/${id}`),
  createArticle: (data: any) => apiClient.post('/articles', data),
  updateArticle: (id: number, data: any) => apiClient.put(`/articles/${id}`, data),
  publishArticle: (id: number, published: boolean) =>
    apiClient.patch(`/articles/${id}/publish`, { published }),
  deleteArticle: (id: number) => apiClient.delete(`/articles/${id}`),
  search: (query: string, limit = 20, offset = 0) =>
    apiClient.get('/articles/search/query', { params: { q: query, limit, offset } }),
  getHomeHighlights: (heroLimit = 3, trendingLimit = 5) =>
    apiClient.get('/articles/home-highlights', { params: { heroLimit, trendingLimit } }),
  getFeaturedArticles: () => apiClient.get('/articles/featured/public'),
  getTrendingArticles: (limit = 5, days = 30) =>
    apiClient.get('/articles/trending/top', { params: { limit, days } }),

  // Likes
  getLikeCount: (articleId: number) =>
    apiClient.get(`/articles/${articleId}/likes`),
  getLikeStatus: (articleId: number) =>
    apiClient.get(`/articles/${articleId}/liked`),
  likeArticle: (articleId: number) =>
    apiClient.post(`/articles/${articleId}/like`),
  unlikeArticle: (articleId: number) =>
    apiClient.delete(`/articles/${articleId}/like`),

  // Similar articles
  getSimilarArticles: (articleId: number, limit = 3) =>
    apiClient.get(`/articles/${articleId}/similar`, { params: { limit } }),
};

// Opinion APIs
export const opinionAPI = {
  getOpinions: (limit = 6, offset = 0) =>
    apiClient.get('/opinions', { params: { limit, offset } }),
  getOpinionBySlug: (slug: string) => apiClient.get(`/opinions/${slug}`),
  getMySubmissions: () => apiClient.get('/opinions/me/submissions'),
  createOpinion: (data: { title: string; excerpt: string; content: string; image_url?: string | null }) =>
    apiClient.post('/opinions', data),
  updateOpinion: (id: number, data: { title?: string; excerpt?: string; content?: string; image_url?: string | null }) =>
    apiClient.put(`/opinions/${id}`, data),
  deleteOpinion: (id: number) => apiClient.delete(`/opinions/${id}`),
};

// Admin APIs
export const adminAPI = {
  getAggregatedArticles: (approved = false) =>
    apiClient.get('/admin/articles/aggregated', { params: { approved } }),
  approveArticle: (id: number, approved: boolean) =>
    apiClient.patch(`/admin/articles/aggregated/${id}`, { approved }),
  promoteAggregatedArticle: (id: number, published = false) =>
    apiClient.post(`/admin/articles/aggregated/${id}/promote`, { published }),
  updateAggregatedArticle: (id: number, data: any) =>
    apiClient.put(`/admin/articles/aggregated/${id}`, data),
  deleteAggregatedArticle: (id: number) =>
    apiClient.delete(`/admin/articles/aggregated/${id}`),
  getFeaturedArticles: () => apiClient.get('/admin/featured'),
  getFeaturedLimits: () => apiClient.get('/admin/limits'),
  getFeaturedMode: () => apiClient.get('/admin/featured/mode'),
  setFeaturedMode: (mode: 'manual' | 'auto' | 'hybrid') => apiClient.patch('/admin/featured/mode', { mode }),
  uploadImage: (data: { fileData: string; fileName: string }) =>
    apiClient.post('/admin/images/upload', data),
  updateFeaturedArticle: (id: number, data: any) =>
    apiClient.patch(`/admin/featured/${id}`, data),
  createFeaturedArticle: (data: any) =>
    apiClient.post('/admin/featured', data),
  deleteFeaturedArticle: (id: number) =>
    apiClient.delete(`/admin/featured/${id}`),
  syncNews: () => apiClient.post('/admin/sync'),
  getStats: () => apiClient.get('/admin/stats'),
  getPendingWriters: () => apiClient.get('/admin/writers/pending'),
  getWriters: () => apiClient.get('/admin/writers'),
  reviewWriter: (id: number, data: { status: 'pending' | 'approved' | 'rejected'; admin_note?: string | null }) =>
    apiClient.patch(`/admin/writers/${id}/review`, data),
  suspendWriter: (id: number, data: { suspended: boolean; admin_note?: string | null }) =>
    apiClient.patch(`/admin/writers/${id}/suspend`, data),
  getWriterContentStats: (id: number) => apiClient.get(`/admin/writers/${id}/content-stats`),
  deleteWriterOpinions: (id: number) => apiClient.delete(`/admin/writers/${id}/opinions`),
  deleteWriterArticles: (id: number) => apiClient.delete(`/admin/writers/${id}/articles`),
  deleteWriter: (id: number) => apiClient.delete(`/admin/writers/${id}`),
  getPendingOpinions: () => apiClient.get('/admin/opinions/pending'),
  getOpinions: (params?: { status?: 'pending' | 'approved' | 'rejected'; author_id?: number }) =>
    apiClient.get('/admin/opinions', { params }),
  reviewOpinion: (id: number, data: { status: 'pending' | 'approved' | 'rejected'; admin_note?: string | null }) =>
    apiClient.patch(`/admin/opinions/${id}/review`, data),
};

// Ads APIs
export const adsAPI = {
  getPlacement: (placement: AdPlacement, category?: string, limit = 1) =>
    apiClient.get(`/ads/placement/${placement}`, { params: { category, limit } }),
  recordImpression: (id: number) => apiClient.post(`/ads/${id}/impression`),
  recordClick: (id: number) => apiClient.post(`/ads/${id}/click`),
  getAll: (placement?: string, active?: boolean) =>
    apiClient.get('/admin/ads', { params: { placement, active } }),
  getStats: () => apiClient.get('/admin/ads/stats'),
  create: (data: any) => apiClient.post('/admin/ads', data),
  update: (id: number, data: any) => apiClient.put(`/admin/ads/${id}`, data),
  toggle: (id: number, active: boolean) => apiClient.patch(`/admin/ads/${id}/toggle`, { active }),
  remove: (id: number) => apiClient.delete(`/admin/ads/${id}`),
};

// User APIs
export const userAPI = {
  getUserProfile: (userId: number) =>
    apiClient.get(`/auth/users/${userId}/profile`),
  updateProfile: (userId: number, data: any) =>
    apiClient.put(`/auth/users/${userId}/profile`, data),

  // Followers
  getFollowers: (userId: number, limit = 20, offset = 0) =>
    apiClient.get(`/users/${userId}/followers`, { params: { limit, offset } }),
  getFollowing: (userId: number, limit = 20, offset = 0) =>
    apiClient.get(`/users/${userId}/following`, { params: { limit, offset } }),
  checkFollowStatus: (userId: number) =>
    apiClient.get(`/users/${userId}/is-following`),
  followUser: (userId: number) =>
    apiClient.post(`/users/${userId}/follow`),
  unfollowUser: (userId: number) =>
    apiClient.delete(`/users/${userId}/follow`),
};

// Newsletter APIs
export const newsletterAPI = {
  subscribe: (email: string, name?: string) =>
    apiClient.post('/newsletter/subscribe', { email, name }),
  unsubscribe: (email: string) =>
    apiClient.post('/newsletter/unsubscribe', { email }),
  verify: (token: string) =>
    apiClient.get(`/newsletter/verify/${token}`),
};

export default apiClient;
